/* eslint-disable no-unused-vars */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import PersonaChat from './PersonaChat';
import QRCode from 'qrcode';
import './PersonaQuestions.css';

const PersonaQuestions = ({ onComplete }) => {
  const navigate = useNavigate();
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAnswers, setRecordedAnswers] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [personaData, setPersonaData] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [showChat, setShowChat] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [liveChatUrl, setLiveChatUrl] = useState(null);
  const [profileLinks, setProfileLinks] = useState({
    linkedin: '',
    facebook: '',
    website: ''
  });

  const audioRef = useRef(null);
  const audioChunksRef = useRef([]);

  const questions = [
    {
      id: 'name',
      question: "What's your name?",
      placeholder: "Tell us your name and how you'd like to be addressed"
    },
    {
      id: 'interests',
      question: "What are your main interests and hobbies?",
      placeholder: "Share what you enjoy doing in your free time"
    },
    {
      id: 'personality',
      question: "How would you describe your personality?",
      placeholder: "Tell us about your character traits and how you interact with others"
    },
    {
      id: 'goals',
      question: "What are your current goals and aspirations?",
      placeholder: "Share what you're working towards or hope to achieve"
    },
    {
      id: 'communication',
      question: "How do you prefer to communicate?",
      placeholder: "Tell us about your communication style and preferences"
    },
    {
      id: 'background',
      question: "Tell us about your background and experience",
      placeholder: "Share relevant information about your professional or personal background"
    }
  ];

  // Initialize media recorder
  useEffect(() => {
    let stream = null;
    
    const initMediaRecorder = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 44100
          } 
        });
        
        // Try different MIME types for better browser compatibility
        let mimeType = 'audio/webm;codecs=opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/webm';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = 'audio/mp4';
            if (!MediaRecorder.isTypeSupported(mimeType)) {
              mimeType = 'audio/wav';
              if (!MediaRecorder.isTypeSupported(mimeType)) {
                mimeType = ''; // Let browser choose
              }
            }
          }
        }
        
        
        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
        
        setMediaRecorder(recorder);

        recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          setAudioChunks([...audioChunksRef.current]);
        }
        };

        recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
        
        if (blob.size > 0) {
          setAudioBlob(blob);
          const url = URL.createObjectURL(blob);
          setAudioUrl(url);
        } else {
          alert('Recording failed - no audio data captured. Please try again.');
        }
          
          // Clear chunks
          audioChunksRef.current = [];
          setAudioChunks([]);
        };

        recorder.onerror = (event) => {
          console.error('MediaRecorder error:', event.error);
        };

        recorder.onstart = () => {
          // Recording started
        };

      } catch (error) {
        console.error('Error accessing microphone:', error);
        // alert('Error accessing microphone. Please check your permissions and try again.');
      }
    };

    initMediaRecorder();

    // Cleanup function
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Recording duration timer
  useEffect(() => {
    let interval = null;
    if (isRecording && recordingStartTime) {
      interval = setInterval(() => {
        setRecordingDuration(Math.floor((Date.now() - recordingStartTime) / 1000));
      }, 1000);
    } else if (!isRecording) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isRecording, recordingStartTime]);

  const startRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'inactive') {
      // Clear both ref and state
      audioChunksRef.current = [];
      setAudioChunks([]);
      setAudioBlob(null);
      setAudioUrl(null);
      setTranscript('');
      setRecordingStartTime(Date.now());
      setRecordingDuration(0);
      setIsRecording(true);
      
      try {
        mediaRecorder.start(1000); // Collect data every second
      } catch (error) {
        console.error('Error starting recording:', error);
        setIsRecording(false);
        alert('Error starting recording. Please try again.');
      }
    } else {
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      setIsRecording(false);
      setRecordingStartTime(null);
      
      try {
        mediaRecorder.stop();
      } catch (error) {
        console.error('Error stopping recording:', error);
        alert('Error stopping recording. Please try again.');
      }
    } else {
    }
  };

  const playRecording = () => {
    if (audioRef.current && audioUrl) {
      audioRef.current.play();
    }
  };

  const processRecording = async () => {
    if (!audioBlob) return;

    setIsProcessing(true);
    try {
      // Use API service to process audio for all questions at once
      const result = await apiService.processAudio(
        audioBlob, 
        'all_questions', 
        'Complete persona interview'
      );
      
      setTranscript(result.transcript);
      
      // Analyze which questions were answered based on the transcript
      const answeredQuestions = analyzeAnsweredQuestions(result.transcript, questions);
      
      // Store answers for all questions that were answered
      // The backend will use LLM to extract names and validate completeness
      const timestamp = new Date().toISOString();
      const newAnswers = {};
      answeredQuestions.forEach(questionId => {
        newAnswers[questionId] = {
          transcript: result.transcript,
          audioUrl: result.audio_url || audioUrl,
          timestamp: timestamp
        };
      });
      
      
      setRecordedAnswers(newAnswers);

    } catch (error) {
      console.error('Error processing recording:', error);
      alert('Error processing recording. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Note: Name extraction is now handled by LLM in the backend
  // This provides much more accurate and reliable name extraction

  // Function to analyze which questions were answered based on transcript
  const analyzeAnsweredQuestions = (transcript, questions) => {
    const answeredQuestions = [];
    const transcriptLower = transcript.toLowerCase();
    
    questions.forEach(question => {
      const questionKeywords = getQuestionKeywords(question);
      const isAnswered = checkIfQuestionAnswered(transcriptLower, questionKeywords, question);
      
      if (isAnswered) {
        answeredQuestions.push(question.id);
      }
    });
    
    return answeredQuestions;
  };

  // Get keywords for each question to help identify if it was answered
  const getQuestionKeywords = (question) => {
    const keywordMap = {
      'name': ['name', 'called', 'i am', 'my name is', 'i\'m', 'call me'],
      'interests': ['like', 'love', 'enjoy', 'hobby', 'hobbies', 'interested', 'passion', 'favorite'],
      'personality': ['personality', 'character', 'traits', 'kind of person', 'describe myself', 'who i am'],
      'goals': ['goal', 'want', 'hope', 'aspire', 'achieve', 'working towards', 'plan'],
      'communication': ['communicate', 'talk', 'speak', 'prefer', 'style', 'way of talking'],
      'background': ['background', 'experience', 'work', 'job', 'profession', 'studied', 'education']
    };
    
    return keywordMap[question.id] || [];
  };

  // Check if a specific question was answered
  const checkIfQuestionAnswered = (transcript, keywords, question) => {
    // Check for direct keyword matches
    const hasKeywords = keywords.some(keyword => transcript.includes(keyword));
    
    // Check for question-specific patterns
    const questionPatterns = getQuestionPatterns(question);
    const hasPatterns = questionPatterns.some(pattern => {
      const regex = new RegExp(pattern, 'i');
      return regex.test(transcript);
    });
    
    // Check for response length (if user gave a substantial answer)
    const responseLength = transcript.length;
    const hasSubstantialResponse = responseLength > 30; // At least 30 characters
    
    // Check for question-specific content indicators
    const hasContentIndicators = checkContentIndicators(transcript, question);
    
    // A question is considered answered if:
    // 1. It has relevant keywords OR patterns OR content indicators, AND
    // 2. The response is substantial enough
    const isAnswered = (hasKeywords || hasPatterns || hasContentIndicators) && hasSubstantialResponse;
    
    
    return isAnswered;
  };

  // Check for content indicators specific to each question
  const checkContentIndicators = (transcript, question) => {
    const contentMap = {
      'name': () => {
        // Look for personal identifiers with full name support
        const namePatterns = [
          /\b(?:my name is|i am|i'm|call me|i go by)\s+([a-zA-Z]+(?:\s+[a-zA-Z]+)*)/i,
          /\b(?:this is|it's)\s+([a-zA-Z]+(?:\s+[a-zA-Z]+)*)/i,
          /\b(?:name's|i'm called|people call me)\s+([a-zA-Z]+(?:\s+[a-zA-Z]+)*)/i
        ];
        return namePatterns.some(pattern => pattern.test(transcript));
      },
      'interests': () => {
        // Look for activity/hobby mentions
        const interestPatterns = [
          /\b(?:i like|i love|i enjoy|i'm into|i'm passionate about)\s+\w+/i,
          /\b(?:my hobby|my hobbies|my interests)\s+\w+/i,
          /\b(?:i do|i practice|i play|i read|i watch)\s+\w+/i
        ];
        return interestPatterns.some(pattern => pattern.test(transcript));
      },
      'personality': () => {
        // Look for self-descriptive words
        const personalityWords = ['outgoing', 'shy', 'friendly', 'quiet', 'energetic', 'calm', 'creative', 'logical', 'organized', 'spontaneous'];
        return personalityWords.some(word => transcript.includes(word));
      },
      'goals': () => {
        // Look for future-oriented statements
        const goalPatterns = [
          /\b(?:i want|i hope|i plan|i aim|i'm trying)\s+\w+/i,
          /\b(?:my goal|my dream|my aspiration)\s+\w+/i,
          /\b(?:i'm working on|i'm focused on)\s+\w+/i
        ];
        return goalPatterns.some(pattern => pattern.test(transcript));
      },
      'communication': () => {
        // Look for communication style mentions
        const commPatterns = [
          /\b(?:i prefer|i like to|i usually|my style)\s+\w+/i,
          /\b(?:i communicate|i talk|i speak)\s+\w+/i,
          /\b(?:direct|indirect|formal|casual|brief|detailed)/i
        ];
        return commPatterns.some(pattern => pattern.test(transcript));
      },
      'background': () => {
        // Look for professional/educational mentions
        const backgroundPatterns = [
          /\b(?:i work|i'm a|i do|my job)\s+\w+/i,
          /\b(?:i studied|i graduated|i have a degree)\s+\w+/i,
          /\b(?:my background|my experience|i have experience)\s+\w+/i
        ];
        return backgroundPatterns.some(pattern => pattern.test(transcript));
      }
    };
    
    const checker = contentMap[question.id];
    return checker ? checker() : false;
  };

  // Get specific patterns for each question type
  const getQuestionPatterns = (question) => {
    const patternMap = {
      'name': [
        'my name is\\s+([a-zA-Z][a-zA-Z\\s\\.\'-]*)',
        'i am\\s+([a-zA-Z][a-zA-Z\\s\\.\'-]*)',
        'i\'m\\s+([a-zA-Z][a-zA-Z\\s\\.\'-]*)',
        'call me\\s+([a-zA-Z][a-zA-Z\\s\\.\'-]*)',
        'i go by\\s+([a-zA-Z][a-zA-Z\\s\\.\'-]*)',
        'name\'s\\s+([a-zA-Z][a-zA-Z\\s\\.\'-]*)',
        'i\'m called\\s+([a-zA-Z][a-zA-Z\\s\\.\'-]*)',
        'people call me\\s+([a-zA-Z][a-zA-Z\\s\\.\'-]*)'
      ],
      'interests': [
        'i like\\s+\\w+',
        'i love\\s+\\w+',
        'i enjoy\\s+\\w+',
        'my hobby is\\s+\\w+',
        'i\'m interested in\\s+\\w+'
      ],
      'personality': [
        'i am\\s+\\w+',
        'i\'m\\s+\\w+',
        'i would describe myself as\\s+\\w+',
        'i\'m the kind of person who\\s+\\w+'
      ],
      'goals': [
        'i want to\\s+\\w+',
        'i hope to\\s+\\w+',
        'my goal is to\\s+\\w+',
        'i\'m working towards\\s+\\w+'
      ],
      'communication': [
        'i prefer to\\s+\\w+',
        'i like to\\s+\\w+',
        'my communication style\\s+\\w+',
        'i usually\\s+\\w+'
      ],
      'background': [
        'i work as\\s+\\w+',
        'i studied\\s+\\w+',
        'my background is\\s+\\w+',
        'i have experience in\\s+\\w+'
      ]
    };
    
    return patternMap[question.id] || [];
  };

  const handleProfileLinkChange = (platform, value) => {
    setProfileLinks(prev => ({
      ...prev,
      [platform]: value
    }));
  };

  const createPersona = () => {
    if (Object.keys(recordedAnswers).length === 0) {
      alert('Please record your answers first.');
      return;
    }
    createPersonaFromAnswers();
  };

  const createPersonaFromAnswers = async () => {
    setIsProcessing(true);
    try {
      // Use API service to create persona
      const result = await apiService.createPersona(recordedAnswers, questions, profileLinks);
      setPersonaData(result);
      
      // Generate live chat URL and QR code
      const baseUrl = window.location.origin;
      const chatUrl = `${baseUrl}/chat/${result.persona_id}`;
      setLiveChatUrl(chatUrl);
      
      // Generate QR code
      try {
        const qrCodeDataUrl = await QRCode.toDataURL(chatUrl, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setQrCodeUrl(qrCodeDataUrl);
      } catch (qrError) {
        console.error('Error generating QR code:', qrError);
      }
      
      setIsCompleted(true);
    } catch (error) {
      console.error('Error creating persona:', error);
      alert('Error creating persona. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const resetForm = () => {
    setRecordedAnswers({});
    setPersonaData(null);
    setIsCompleted(false);
    setAudioBlob(null);
    setAudioUrl(null);
    setTranscript('');
    setRecordingDuration(0);
    setProfileLinks({
      linkedin: '',
      facebook: '',
      website: ''
    });
  };

  if (showChat && personaData) {
    return <PersonaChat persona={personaData} onBack={() => setShowChat(false)} />;
  }

  if (isCompleted && personaData) {
    return (
      <div className="persona-completed">
        {qrCodeUrl && (
          <div className="qr-code-section">
            <h3>Share Your Persona</h3>
            <div className="qr-code-container">
              <img src={qrCodeUrl} alt="QR Code for Live Chat" className="qr-code-image" />
              <p className="qr-code-description">Scan this QR code to access your persona's live chat</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="persona-questions">
      <div className="questions-container">
        <div className="questions-sidebar">
          <h2>Create Your Persona</h2>
          <div className="instructions">
            <p><strong>Answer all questions in one recording</strong></p>
            <p>The more questions you answer, the more personalized your persona will be. Please try to answer all questions if possible.</p>
            {Object.keys(recordedAnswers).length > 0 && (
              <div className="progress-summary">
                <p><strong>Progress: {Object.keys(recordedAnswers).length}/{questions.length} questions answered</strong></p>
              </div>
            )}
          </div>

          <div className="questions-list">
            {questions.map((q, index) => (
              <div 
                key={q.id}
                className={`question-item ${recordedAnswers[q.id] ? 'completed' : ''}`}
              >
                <div className="question-number">
                  {recordedAnswers[q.id] ? '✓' : index + 1}
                </div>
                <div className="question-text">
                  <strong>{q.question}</strong>
                  <p className="question-placeholder">{q.placeholder}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="recording-panel">
          <div className="recording-instructions">
            <h3>Record Your Complete Answer</h3>
            <p>Please answer all the questions on the left in one continuous recording. Take your time to provide thoughtful responses to each question.</p>
          </div>

          <div className="profile-links-section">
            <h3>Optional: Your Profile Links</h3>
            <p>Add your social media and website links to enhance your persona.</p>
            <div className="profile-links-form">
              <div className="profile-link-input">
                <label htmlFor="linkedin">LinkedIn Profile:</label>
                <input
                  type="url"
                  id="linkedin"
                  value={profileLinks.linkedin}
                  onChange={(e) => handleProfileLinkChange('linkedin', e.target.value)}
                  placeholder="https://linkedin.com/in/your-profile"
                  className="profile-link-field"
                />
              </div>
              <div className="profile-link-input">
                <label htmlFor="facebook">Facebook Profile:</label>
                <input
                  type="url"
                  id="facebook"
                  value={profileLinks.facebook}
                  onChange={(e) => handleProfileLinkChange('facebook', e.target.value)}
                  placeholder="https://facebook.com/your-profile"
                  className="profile-link-field"
                />
              </div>
              <div className="profile-link-input">
                <label htmlFor="website">Website:</label>
                <input
                  type="url"
                  id="website"
                  value={profileLinks.website}
                  onChange={(e) => handleProfileLinkChange('website', e.target.value)}
                  placeholder="https://your-website.com"
                  className="profile-link-field"
                />
              </div>
            </div>
          </div>

          <div className="recording-controls">
            {!isRecording && !audioBlob && (
              <div>
                <button 
                  onClick={startRecording}
                  className="btn-record"
                  disabled={!mediaRecorder}
                >
                  Start Recording All Answers
                </button>
                {!mediaRecorder && (
                  <p className="error-message">
                    Microphone not available. Please check your permissions.
                  </p>
                )}
              </div>
            )}

            {isRecording && (
              <div className="recording-active">
                <button 
                  onClick={stopRecording}
                  className="btn-stop"
                >
                  Stop Recording
                </button>
                <div className="recording-timer">
                  <div className="recording-dot"></div>
                  <span>Recording: {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}</span>
                </div>
              </div>
            )}

            {audioBlob && !transcript && (
              <div className="recording-actions">
                <button onClick={playRecording} className="btn-play">
                  Play Recording
                </button>
                <button 
                  onClick={processRecording}
                  className="btn-process"
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Processing...' : 'Process & Transcribe'}
                </button>
              </div>
            )}

            {transcript && (
              <div className="transcript-section">
                <h4>Your Complete Answer:</h4>
                <div className="transcript-text">
                  {transcript}
                </div>
                
                {/* Show which questions were answered */}
                <div className="answered-questions-summary">
                  <h5>Questions Answered ({Object.keys(recordedAnswers).length}/{questions.length}):</h5>
                  <div className="answered-questions-list">
                    {questions.map((q, index) => (
                      <div 
                        key={q.id}
                        className={`answered-question-item ${recordedAnswers[q.id] ? 'answered' : 'not-answered'}`}
                      >
                        <span className="question-status">
                          {recordedAnswers[q.id] ? '✓' : '✗'}
                        </span>
                        <span className="question-text">
                          {q.question}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="transcript-actions">
                  <button onClick={createPersona} className="btn-next" disabled={isProcessing}>
                    {isProcessing ? 'Creating Persona...' : 'Create My Persona'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {audioUrl && (
            <div className="audio-section">
              <audio 
                ref={audioRef}
                src={audioUrl}
                controls
                className="audio-player"
                onError={(e) => {
                  console.error('Audio playback error:', e);
                }}
              />
              <p className="audio-info">
                Audio recorded: {audioBlob ? `${Math.round(audioBlob.size / 1024)} KB` : 'Unknown size'}
                {audioBlob && ` (${audioBlob.type})`}
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default PersonaQuestions;
